/**
 * HT GA4 Tracker v2.5 - PRODUCTION OPTIMIZED
 *
 * Standalone GA4 e-commerce tracking script.
 * Listens for custom events and pushes to GTM dataLayer.
 *
 * EVENTS HANDLED:
 * - `ht:add_to_cart_success`: Calculator products added to cart
 * - `ht:material_selected`: Material selection from carousel
 * - `ht:style_selected`: Style selection from gallery
 * - `ht:template_toggle`: Template accordion interactions
 * - `ht:template_download`: Template file downloads
 * - `cart:opened` / `cart-drawer:opened`: Cart view tracking
 * - `wizard:step-changed`: Wizard step navigation
 * - `wizard:final-complete`: Wizard completion
 * - `artwork:method-selected`: Artwork method selection
 * - `artwork:upload-complete`: Artwork file upload completion
 * - `artwork:cancelled`: Artwork modal abandonment
 * - `calculator:complete`: Price calculation completion
 * - `cart-bridge:error`: Cart/Vercel errors
 * - `reorder:button-clicked`: Reorder button usage
 * - Page Load: Product view tracking
 *
 * @version 2.5.0
 */

'use strict';

class HTGA4Tracker {
  constructor() {
    this.gtmEnabled = !!window.dataLayer;
    
    if (!this.gtmEnabled) return;
    
    this.init();
  }
  
  init() {
    this.bindEvents();
    this.trackViewItemOnLoad();
  }

  bindEvents() {
    document.addEventListener('ht:add_to_cart_success', (e) => this.trackAddToCart(e.detail));
    document.addEventListener('cart:opened', () => this.trackViewCart());
    document.addEventListener('cart-drawer:opened', () => this.trackViewCart());
    document.addEventListener('ht:material_selected', (e) => this.trackMaterialSelection(e.detail));
    document.addEventListener('ht:style_selected', (e) => this.trackStyleSelection(e.detail));
    document.addEventListener('ht:template_toggle', (e) => this.trackTemplateToggle(e.detail));
    document.addEventListener('ht:template_download', (e) => this.trackTemplateDownload(e.detail));

    // Wizard funnel tracking
    document.addEventListener('wizard:step-changed', (e) => this.trackWizardStep(e.detail));
    document.addEventListener('wizard:final-complete', (e) => this.trackWizardCompleted(e.detail));

    // Artwork method tracking
    document.addEventListener('artwork:method-selected', (e) => this.trackArtworkMethod(e.detail));

    // Reorder tracking
    document.addEventListener('reorder:button-clicked', (e) => this.trackReorderClick(e.detail));

    // Price calculation tracking
    document.addEventListener('calculator:complete', (e) => this.trackPriceCalculated(e.detail));

    // Artwork flow tracking (upload + abandonment)
    document.addEventListener('artwork:upload-complete', (e) => this.trackArtworkUpload(e.detail));
    document.addEventListener('artwork:cancelled', (e) => this.trackArtworkAbandoned(e.detail));

    // Error tracking
    document.addEventListener('cart-bridge:error', (e) => this.trackCartError(e.detail));
  }

  trackMaterialSelection(detail) {
    if (!detail) return;
    
    try {
      this.pushToDataLayer({
        event: 'select_material',
        material_value: detail.material_value,
        material_name: detail.material_name,
        base_value: detail.base_value,
        section_id: detail.section_id
      });
    } catch (e) {
      console.error('GA4 Error: Material selection tracking failed', e);
    }
  }

  trackStyleSelection(detail) {
    if (!detail) return;
    
    try {
      this.pushToDataLayer({
        event: 'select_style',
        style_handle: detail.style_handle,
        style_name: detail.style_name,
        style_system_id: detail.style_system_id,
        product_type: detail.product_type,
        calculator_type: detail.calculator_type,
        section_id: detail.section_id
      });
    } catch (e) {
      console.error('GA4 Error: Style selection tracking failed', e);
    }
  }

  trackAddToCart(detail) {
    if (!detail || !detail.calculatorData || !detail.vercelResponse) return;

    try {
      const { calculatorData: state, vercelResponse, productName } = detail;
      const finalPrice = state._price_total_pence / 100;
      const realQuantity = parseInt(state.quantity, 10);

      this.pushToDataLayer({
        event: 'add_to_cart',
        ecommerce: {
          currency: 'GBP',
          value: finalPrice,
          items: [{
            item_id: vercelResponse.variantId,
            item_name: productName,
            price: parseFloat((finalPrice / realQuantity).toFixed(4)),
            quantity: realQuantity,
            item_real_quantity: realQuantity,
            item_material: state.material || 'N/A',
            item_shape: state.shape || 'N/A',
            item_size: (state.size === 'custom_size' || state.shape === 'custom') ? 'Custom Size' : state.size,
            item_custom_dimensions: state.custom_size ? `${state.custom_size.width}x${state.custom_size.height}mm` : 'N/A',
            item_print_sides: state.print_sides || 'N/A',
            item_print_ink: state.print_ink || 'N/A',
            item_corner_style: state.corner_style || 'None',
            item_drill_hole: (state.drill_diameter && state.drill_diameter !== '0' && state.drill_diameter !== 'none') ? 'Yes' : 'No',
            item_drill_diameter: state.drill_diameter || '0',
            item_eyelet: state.eyelet || 'None',
            item_string_attachment: state.string_attachment || 'None',
            item_string_type: state.string_type || 'None',
            item_perforation: state.perforation ? 'Yes' : 'No',
            item_multiple_designs: state.multiple_designs || 1,
            item_variable_data: state.variable_data ? 'Yes' : 'No',
            item_sequential_numbers: state.sequential_data ? 'Yes' : 'No',
            item_artwork_method: state.artworkMethod || 'email_later',
            item_design_service: state.designService ? 'Yes' : 'No',
            item_calculator_type: state.calculatorType || 'basic'
          }]
        }
      });
    } catch (e) {
      console.error('GA4 Error: Add to cart tracking failed', e);
    }
  }

  trackViewItemOnLoad() {
    if (window.Shopify && typeof window.Shopify.product === 'object' && window.Shopify.product !== null) {
      const product = window.Shopify.product;
      const initialPrice = (product.price || 0) / 100;
      
      this.pushToDataLayer({
        event: 'view_item',
        ecommerce: {
          currency: 'GBP',
          value: initialPrice,
          items: [{
            item_id: product.id.toString(),
            item_name: product.title,
            price: initialPrice,
            quantity: 1
          }]
        }
      });
    }
  }

  trackViewCart() {
    fetch('/cart.js')
      .then(res => res.json())
      .then(cart => {
        if (!cart || !cart.items || cart.items.length === 0) return;

        this.pushToDataLayer({
          event: 'view_cart',
          ecommerce: {
            currency: 'GBP',
            value: cart.total_price / 100,
            items: cart.items.map(item => ({
              item_id: item.variant_id.toString(),
              item_name: item.product_title,
              price: item.price / 100,
              quantity: item.quantity
            }))
          }
        });
      })
      .catch(e => console.error('GA4 Error: View cart tracking failed', e));
  }

  trackTemplateToggle(detail) {
    if (!detail) return;

    try {
      this.pushToDataLayer({
        event: 'template_interaction',
        interaction_type: 'toggle',
        element_type: detail.element_type,
        action: detail.action,
        shape: detail.shape || 'N/A',
        format: detail.format || 'N/A'
      });
    } catch (e) {
      console.error('GA4 Error: Template toggle tracking failed', e);
    }
  }

  trackTemplateDownload(detail) {
    if (!detail || !detail.file_name) return;

    try {
      this.pushToDataLayer({
        event: 'file_download',
        file_extension: detail.format || 'unknown',
        file_name: detail.file_name,
        link_url: detail.file_url,
        link_domain: new URL(detail.file_url).hostname,
        link_text: detail.file_name,
        shape: detail.shape || 'N/A',
        format: detail.format || 'N/A',
        outbound: false
      });
    } catch (e) {
      console.error('GA4 Error: Template download tracking failed', e);
    }
  }

  trackWizardStep(detail) {
    if (!detail) return;

    try {
      this.pushToDataLayer({
        event: 'wizard_step_viewed',
        wizard_step: detail.step,
        calculator_type: detail.calculatorType || 'unknown'
      });
    } catch (e) {
      console.error('GA4 Error: Wizard step tracking failed', e);
    }
  }

  trackWizardCompleted(detail) {
    if (!detail) return;

    try {
      this.pushToDataLayer({
        event: 'wizard_completed',
        wizard_step: detail.step,
        calculator_type: detail.calculatorType || 'unknown',
        is_valid: detail.isValid !== false
      });
    } catch (e) {
      console.error('GA4 Error: Wizard completion tracking failed', e);
    }
  }

  trackArtworkMethod(detail) {
    if (!detail) return;

    try {
      this.pushToDataLayer({
        event: 'artwork_method_selected',
        artwork_method: detail.method || 'unknown',
        calculator_type: detail.calculatorType || 'unknown'
      });
    } catch (e) {
      console.error('GA4 Error: Artwork method tracking failed', e);
    }
  }

  trackReorderClick(detail) {
    try {
      this.pushToDataLayer({
        event: 'reorder_button_clicked',
        order_id: detail?.orderId || 'unknown',
        order_name: detail?.orderName || 'unknown',
        line_item_count: detail?.lineItemCount || 1
      });
    } catch (e) {
      console.error('GA4 Error: Reorder click tracking failed', e);
    }
  }

  trackPriceCalculated(detail) {
    if (!detail) return;

    try {
      this.pushToDataLayer({
        event: 'price_calculated',
        calculator_type: detail.calculatorType || 'unknown',
        product_type: detail.productType || 'swing-tags',
        total_price_pence: detail.pricing?.total_price_pence || detail.pricing?._price_total_pence || 0,
        quantity: detail.pricing?.quantity || 0,
        is_valid: detail.isValid !== false
      });
    } catch (e) {
      console.error('GA4 Error: Price calculated tracking failed', e);
    }
  }

  trackArtworkUpload(detail) {
    if (!detail) return;

    try {
      this.pushToDataLayer({
        event: 'artwork_upload_completed',
        file_count: detail.files?.length || 1,
        calculator_type: detail.calculatorType || 'unknown'
      });
    } catch (e) {
      console.error('GA4 Error: Artwork upload tracking failed', e);
    }
  }

  trackArtworkAbandoned(detail) {
    try {
      this.pushToDataLayer({
        event: 'artwork_modal_abandoned',
        calculator_type: detail?.calculatorType || 'unknown',
        time_in_modal_ms: detail?.timeInModal || 0
      });
    } catch (e) {
      console.error('GA4 Error: Artwork abandoned tracking failed', e);
    }
  }

  trackCartError(detail) {
    if (!detail) return;

    try {
      this.pushToDataLayer({
        event: 'cart_error',
        error_type: detail.type || 'unknown',
        error_message: detail.error || 'N/A',
        calculator_type: detail.calculatorData?.calculatorType || 'unknown'
      });
    } catch (e) {
      console.error('GA4 Error: Cart error tracking failed', e);
    }
  }

  pushToDataLayer(data) {
    try {
      window.dataLayer = window.dataLayer || [];
      
      if (data.ecommerce) {
        window.dataLayer.push({ ecommerce: null });
      }
      
      window.dataLayer.push(data);
    } catch (e) {
      console.error('GA4 Error: DataLayer push failed', e);
    }
  }
}

// Initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.htGA4Tracker = new HTGA4Tracker();
  });
} else {
  window.htGA4Tracker = new HTGA4Tracker();
}